import { useEffect, useRef, useState } from "react";
import "./settings.css";
import { useContext } from "react";
import { UserContext } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";
import { LoaderContext } from "../../context/LoaderContext";
import Navbar from "../../components/navbar/navbar";
import Loader from "../../components/loader/loader";
import Footer from "../../components/Footer/Footer";
import SideNav from "../../components/SideNav/sideNav";
import { DELETE } from "../../utils/httpUtils";
import ArrowSvg from "../../assets/svg/arrowSvg";
import { SettingOption } from "../../components/SettingOption/setting-option";
import { ConfirmationModal } from "../../components/Modals/ConfirmationModal/confirmation-modal";
import Accordion from "../../components/Accoridon/Accordion";
import AccountSettings from "./AccountSettings/AccountSettings";
import NotificationSettings from "./NotificationSettings/NotificationSettings";
import SubscriptionSettings from "./SubscriptionSettings/SubscriptionSettings";
import DeveloperSettings from "./DeveloperSettings/DeveloperSettings";
import PrivacySettings from "./PrivacySettings/PrivacySettings";
import { AlertContext } from "../../context/AlertContext";
import { Alert, isMobileDevice, RevokeAllSessionUrls } from "../../utils/genericUtils";
import InterfaceSettings from "./InterfaceSettings/InterfaceSettings";
import StorageSettings from "./StorageSettings/StorageSettings";

import ProdIgnore from "../../components/ProductionIgnore/prod-ignore";

function Settings() {
  const { user, loginUser } = useContext(UserContext);
  const { show, showloader, hideloader } = useContext(LoaderContext);
  const { globalAlert } = useContext(AlertContext);
  const [sideNavOpen, setSideNavOpen] = useState(true);
  const [showDeleteDataModal, setShowDeleteDataModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const router = useNavigate();

  //refs
  const settingsHeader = useRef(null);

  useEffect(() => {}, [showDeleteAccountModal]);

  if (!user) return;

  function ToDashboard() {
    router("/dashboard");
  }

  function ToHome() {
    router("/");
  }

  const deleteDataModalId = "delete-data-confirmation-modal";
  const deleteAccountModalId = "delete-account-confirmation-modal";

  function ShowDeleteDataModal() {
    setShowDeleteDataModal(true);
  }

  function HideDeleteDataModal() {
    setShowDeleteDataModal(false);
  }

  function ShowDeleteAccountModal() {
    setShowDeleteAccountModal(true);
  }

  function HideDeleteAccountModal() {
    setShowDeleteAccountModal(false);
  }

  async function DeleteAccountData() {
    HideDeleteDataModal();
    showloader();
    RevokeAllSessionUrls();
    let delete_data_Res = await DELETE("/delete-account-data", undefined, user);
    hideloader();
    if (!delete_data_Res) {
      Alert(globalAlert, "success", "Success", "Account data successfully deleted");
    } else {
      Alert(globalAlert, "error", "Error", delete_data_Res.error.message ?? "unkown error");
    }
  }

  async function DeleteAccount() {
    HideDeleteAccountModal();
    showloader();
    RevokeAllSessionUrls();
    let response = await DELETE("/delete-account", undefined, user);
    hideloader();
    if (!response) {
      loginUser(null);
      localStorage.clear();
      ToHome();
    } else {
      Alert(globalAlert, "error", "Error", response.error.message ?? "unkown error");
    }
  }

  const AccordianSections = [
    {
      title: "Account Info",
      content: <AccountSettings />,
    },
    {
      title: "Subscription Settings",
      content: <SubscriptionSettings />,
    },
    {
      title: "Notification Settings",
      content: <NotificationSettings {...user.settings} />,
    },
    {
      title: "Interface Settings",
      content: <InterfaceSettings {...user.settings} />,
    },
    {
      title: "Privacy & Security",
      content: <PrivacySettings mfaEnabled={user.mfaVerified && user.mfaMethod !== ""} {...user.settings} />,
    },
    {
      title: "Storage Settings",
      content: <StorageSettings />,
    },
    {
      title: "Developer Settings",
      content: (
        <ProdIgnore
          description="Developer settings not yet flushed out"
          altContent={
            <>
              <h3>Coming Soon</h3>
              <div className="setting-option-divider"></div>
              <p className="setting-option-text" style={{ marginBottom: "20px" }}>
                In an upcoming update, Developer Mode will unlock expanded capabilities for integrating with the FileFreak API in your own
                applications.
              </p>
            </>
          }
        >
          <DeveloperSettings {...user.settings} />{" "}
        </ProdIgnore>
      ),
    },
    {
      title: "Data Management",
      content: (
        <>
          {/* DELETE ACCOUNT DATA */}
          <ConfirmationModal
            onClickOut={HideDeleteDataModal}
            showDivider={true}
            animate={true}
            isActive={showDeleteDataModal}
            id={deleteDataModalId}
            includeCheckboxSafety={true}
            safetyText={"I Acknowledge that this action is Permenant."}
            header={"Do you want to Proceed?"}
            message={"Warning this action will delete your account data. This action cannot be reversed."}
            denyText={"I want to Keep Data."}
            confirmText={"I want to Delete my Data"}
            onDeny={HideDeleteDataModal}
            onConfirm={DeleteAccountData}
          />

          {/* DELETE ACCOUNT */}
          <ConfirmationModal
            onClickOut={HideDeleteAccountModal}
            showDivider={true}
            animate={true}
            isActive={showDeleteAccountModal}
            id={deleteAccountModalId}
            includeCheckboxSafety={true}
            safetyText={`I Acknowledge that this action is Permenant.`}
            header={"Do you want to Proceed?"}
            message={"Warning this action will delete your account and all of it's data. This action cannot be reversed."}
            denyText="I want to Keep My Account."
            confirmText="I want to Delete my Account"
            onDeny={HideDeleteAccountModal}
            onConfirm={DeleteAccount}
          />

          {/* DELETE ACCOUNT DATA */}
          <SettingOption
            id={"delete-account-data"}
            controlType={"button"}
            buttonText="Delete All Account Data"
            buttonClass={"btn-orange-inverted"}
            description="Deletes all files and folders within your account, restores the default folders back to your account."
            onClick={ShowDeleteDataModal}
            showDivider={true}
          />

          {/* DELETE ACCOUNT */}
          <SettingOption
            id={"delete-account"}
            controlType={"button"}
            buttonText="Delete Account"
            buttonClass={"btn-red-inverted"}
            description="Completely delete your account and all of it's data, automatically cancels any active subscriptions."
            onClick={ShowDeleteAccountModal}
            showDivider={false}
          />
        </>
      ),
    },
  ];

  return (
    <>
      <Loader />
      <Navbar active={"Dashboard"} />
      <section className="settings-page-bg">
        <SideNav />
        <section className="main-settings">
          {/* header */}
          <div className="settings-header-container" ref={settingsHeader}>
            <h1 className="settings-header">File Freak Settings</h1>
          </div>

          <button className="back-button standard-button" onClick={ToDashboard}>
            <ArrowSvg class={"svg"} />
            Back To Dashboard
          </button>

          {/* Settings List */}
          <section className="settings-list">
            <div className="accordion-wrapper">
              <Accordion id="settings-accordian" animate={false} defaultOpen={true} sections={AccordianSections} />
            </div>
          </section>
        </section>
      </section>
      <Footer />
    </>
  );
}

export default Settings;
